import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  Position,
  PositionCreatePayload,
  PositionUpdatePayload,
  createPosition,
  updatePosition,
} from '../../contracts/services/ContractWizardService';
import { personalakteStyles as ps, colors, shadows } from '../../employees/styles/personalakteStyles';

interface PositionEditorDrawerProps {
  workAreaId: number;
  position: Position | null;
  onClose: () => void;
  onSave: () => void;
}

const EMPLOYMENT_TYPES = [
  { value: 'Trainee', label: 'Trainee' },
  { value: 'kurzfristig', label: 'Kurzfristig beschäftigt' },
  { value: 'Minijob', label: 'Minijob' },
];

function PositionEditorDrawer({ workAreaId, position, onClose, onSave }: PositionEditorDrawerProps): React.JSX.Element {
  const isEdit = !!position;

  const [formData, setFormData] = useState<Partial<Position>>({
    label: '',
    position_key: '',
    allowed_employment_types: [],
    active: true,
    position_title_default: '',
    duties_richtext_default: '',
    default_compensation_type: '',
    default_hourly_rate: undefined,
    default_salary_monthly_cents: undefined,
    default_weekly_hours: undefined,
    default_probation_months: undefined,
    default_notice_weeks: undefined,
    default_notice_text: '',
    default_extra_leave_days_per_year: undefined,
    default_benefits: {
      uniform: false,
      meal_allowance: false,
      service_vehicle: false,
    },
  });

  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (position) {
      setFormData({
        ...position,
        default_benefits: position.default_benefits || {
          uniform: false,
          meal_allowance: false,
          service_vehicle: false,
        },
      });
    }
  }, [position]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleEmploymentTypeToggle = (type: string) => {
    const current = formData.allowed_employment_types || [];
    const newTypes = current.includes(type)
      ? current.filter((t) => t !== type)
      : [...current, type];
    setFormData((prev) => ({ ...prev, allowed_employment_types: newTypes }));
  };

  const handleBenefitToggle = (benefit: string) => {
    setFormData((prev) => ({
      ...prev,
      default_benefits: {
        ...prev.default_benefits,
        [benefit]: !prev.default_benefits?.[benefit],
      },
    }));
  };

  const canonEmploymentTypes = (input?: string[]): string[] => {
    const CANON_ET: Record<string, string> = {
      TRAINEE: 'Trainee',
      KURZFRISTIG: 'kurzfristig',
      MINIJOB: 'Minijob',
      Trainee: 'Trainee',
      trainee: 'Trainee',
      kurzfristig: 'kurzfristig',
      Minijob: 'Minijob',
      minijob: 'Minijob',
    };

    if (!input) return [];
    return input
      .map(t => String(t).trim())
      .filter(Boolean)
      .map(t => CANON_ET[t] ?? CANON_ET[t.toUpperCase()] ?? t)
      .filter((v, i, a) => a.indexOf(v) === i);
  };

  const canonCompType = (input?: string | null): 'hourly' | 'salary' | '' => {
    if (!input) return '';
    const v = String(input).trim().toLowerCase();
    return (v === 'hourly' || v === 'salary') ? v : '';
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.label?.trim()) {
      newErrors.label = 'Label is required';
    }

    if (!formData.position_key?.trim()) {
      newErrors.position_key = 'Position Key is required';
    }

    if (formData.default_compensation_type === 'hourly') {
      if (!formData.default_hourly_rate || formData.default_hourly_rate <= 0) {
        newErrors.default_hourly_rate = 'Hourly Rate is required for hourly compensation';
      }
    }

    if (formData.default_compensation_type === 'salary') {
      if (!formData.default_salary_monthly_cents || formData.default_salary_monthly_cents <= 0) {
        newErrors.default_salary_monthly_cents = 'Monthly Salary is required for salary compensation';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      toast.error('Please fix validation errors');
      return;
    }

    setSaving(true);
    try {
      if (isEdit && position) {
        const payload: PositionUpdatePayload = {
          label: formData.label,
          position_key: formData.position_key,
          allowed_employment_types: canonEmploymentTypes(formData.allowed_employment_types),
          active: formData.active,
          position_title_default: formData.position_title_default,
          duties_richtext_default: formData.duties_richtext_default,
          default_compensation_type: canonCompType(formData.default_compensation_type),
          default_hourly_rate: formData.default_hourly_rate,
          default_salary_monthly_cents: formData.default_salary_monthly_cents,
          default_weekly_hours: formData.default_weekly_hours,
          default_probation_months: formData.default_probation_months,
          default_notice_weeks: formData.default_notice_weeks,
          default_notice_text: formData.default_notice_text,
          default_extra_leave_days_per_year: formData.default_extra_leave_days_per_year,
          default_benefits: formData.default_benefits,
        };
        await updatePosition(position.id, payload);
        toast.success('Position updated successfully');
      } else {
        const payload: PositionCreatePayload = {
          work_area: workAreaId,
          label: formData.label!,
          position_key: formData.position_key!,
          allowed_employment_types: canonEmploymentTypes(formData.allowed_employment_types),
          active: formData.active,
          position_title_default: formData.position_title_default,
          duties_richtext_default: formData.duties_richtext_default,
          default_compensation_type: canonCompType(formData.default_compensation_type),
          default_hourly_rate: formData.default_hourly_rate,
          default_salary_monthly_cents: formData.default_salary_monthly_cents,
          default_weekly_hours: formData.default_weekly_hours,
          default_probation_months: formData.default_probation_months,
          default_notice_weeks: formData.default_notice_weeks,
          default_notice_text: formData.default_notice_text,
          default_extra_leave_days_per_year: formData.default_extra_leave_days_per_year,
          default_benefits: formData.default_benefits,
        };
        await createPosition(payload);
        toast.success('Position created successfully');
      }
      onSave();
      onClose();
    } catch (err: any) {
      console.error('Error saving position:', err);
      const errorMsg = err?.response?.data?.message || err?.response?.data?.detail || 'Failed to save position';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const inputStyle: React.CSSProperties = ps.inputStyle;

  const labelStyle: React.CSSProperties = ps.labelStyle;

  const sectionStyle: React.CSSProperties = {
    marginBottom: 24,
    paddingBottom: 20,
    borderBottom: `1px solid ${colors.border}`,
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: 700,
        backgroundColor: colors.bgCard,
        boxShadow: shadows.elevated,
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '20px 24px',
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: colors.bgCard,
        }}
      >
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: colors.navy }}>
          {isEdit ? 'Edit Position' : 'Add Position'}
        </h2>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            fontSize: 24,
            cursor: 'pointer',
            color: colors.textMuted,
            padding: 0,
          }}
        >
          ×
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: 24, backgroundColor: colors.bgCard }}>
        {/* Basic Info */}
        <div style={sectionStyle}>
          <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: colors.navy }}>
            Basic Information
          </h3>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>
              Label <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="text"
              value={formData.label || ''}
              onChange={(e) => handleInputChange('label', e.target.value)}
              style={{ ...inputStyle, borderColor: errors.label ? 'red' : colors.border }}
              placeholder="e.g., Servicekraft, Produktionskraft"
            />
            {errors.label && <div style={{ color: 'red', fontSize: 12, marginTop: 4 }}>{errors.label}</div>}
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>
              Position Key (slug) <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="text"
              value={formData.position_key || ''}
              onChange={(e) => handleInputChange('position_key', e.target.value.toLowerCase().replace(/\s+/g, '_'))}
              style={{ ...inputStyle, borderColor: errors.position_key ? 'red' : colors.border, fontFamily: 'monospace' }}
              placeholder="e.g., service_staff"
            />
            {errors.position_key && <div style={{ color: 'red', fontSize: 12, marginTop: 4 }}>{errors.position_key}</div>}
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Employment Types</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {EMPLOYMENT_TYPES.map((type) => (
                <label
                  key={type.value}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '6px 12px',
                    backgroundColor: formData.allowed_employment_types?.includes(type.value) ? colors.infoLight : colors.bgMuted,
                    border: `1px solid ${formData.allowed_employment_types?.includes(type.value) ? colors.info : colors.border}`,
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontSize: 13,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={formData.allowed_employment_types?.includes(type.value) || false}
                    onChange={() => handleEmploymentTypeToggle(type.value)}
                    style={{ marginRight: 6 }}
                  />
                  {type.label}
                </label>
              ))}
            </div>
            <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 6 }}>
              Leave empty to allow all types
            </div>
          </div>

          <div>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.active || false}
                onChange={(e) => handleInputChange('active', e.target.checked)}
                style={{ marginRight: 8 }}
              />
              <span style={{ fontSize: 14, color: colors.navy }}>Active</span>
            </label>
          </div>
        </div>

        {/* Vertrag (Contract) */}
        <div style={sectionStyle}>
          <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: colors.navy }}>
            Vertrag (Contract Defaults)
          </h3>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Position Title Default</label>
            <input
              type="text"
              value={formData.position_title_default || ''}
              onChange={(e) => handleInputChange('position_title_default', e.target.value)}
              style={inputStyle}
              placeholder="e.g., Servicemitarbeiter/in"
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>
              Duties / Role Description (Rich Text)
            </label>
            <textarea
              value={formData.duties_richtext_default || ''}
              onChange={(e) => handleInputChange('duties_richtext_default', e.target.value)}
              style={{ ...inputStyle, minHeight: 100, fontFamily: 'inherit', resize: 'vertical' }}
              placeholder="Enter HTML or plain text description of duties..."
            />
            <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 4 }}>
              This becomes role_description_text in the contract
            </div>
          </div>

          <div style={{ marginBottom: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={labelStyle}>Notice Period (weeks)</label>
              <input
                type="number"
                value={formData.default_notice_weeks || ''}
                onChange={(e) => handleInputChange('default_notice_weeks', e.target.value ? Number(e.target.value) : undefined)}
                style={inputStyle}
                placeholder="e.g., 2"
                min="0"
              />
            </div>
            <div>
              <label style={labelStyle}>Extra Leave Days / Year</label>
              <input
                type="number"
                value={formData.default_extra_leave_days_per_year || ''}
                onChange={(e) => handleInputChange('default_extra_leave_days_per_year', e.target.value ? Number(e.target.value) : undefined)}
                style={inputStyle}
                placeholder="e.g., 0"
                min="0"
              />
            </div>
          </div>

          <div>
            <label style={labelStyle}>Notice Text (Read-only hint for wizard)</label>
            <textarea
              value={formData.default_notice_text || ''}
              onChange={(e) => handleInputChange('default_notice_text', e.target.value)}
              style={{ ...inputStyle, minHeight: 60, fontFamily: 'inherit', resize: 'vertical' }}
              placeholder="Optional notice text to display in wizard..."
            />
          </div>
        </div>

        {/* Vergütung (Compensation) */}
        <div style={sectionStyle}>
          <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: colors.navy }}>
            Vergütung (Compensation Defaults)
          </h3>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Compensation Type</label>
            <select
              value={formData.default_compensation_type || ''}
              onChange={(e) => handleInputChange('default_compensation_type', e.target.value)}
              style={inputStyle}
            >
              <option value="">-- None --</option>
              <option value="hourly">Hourly</option>
              <option value="salary">Salary</option>
            </select>
          </div>

          {formData.default_compensation_type === 'hourly' && (
            <>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>
                  Hourly Rate (€) <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.default_hourly_rate || ''}
                  onChange={(e) => handleInputChange('default_hourly_rate', e.target.value ? parseFloat(e.target.value) : undefined)}
                  style={{ ...inputStyle, borderColor: errors.default_hourly_rate ? 'red' : colors.border }}
                  placeholder="e.g., 13.50"
                  min="0"
                />
                {errors.default_hourly_rate && <div style={{ color: 'red', fontSize: 12, marginTop: 4 }}>{errors.default_hourly_rate}</div>}
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Weekly Hours</label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.default_weekly_hours || ''}
                  onChange={(e) => handleInputChange('default_weekly_hours', e.target.value ? parseFloat(e.target.value) : undefined)}
                  style={inputStyle}
                  placeholder="e.g., 40"
                  min="0"
                />
              </div>
            </>
          )}

          {formData.default_compensation_type === 'salary' && (
            <>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>
                  Monthly Salary (Cents) <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="number"
                  value={formData.default_salary_monthly_cents || ''}
                  onChange={(e) => handleInputChange('default_salary_monthly_cents', e.target.value ? parseInt(e.target.value) : undefined)}
                  style={{ ...inputStyle, borderColor: errors.default_salary_monthly_cents ? 'red' : colors.border }}
                  placeholder="e.g., 300000 (= 3000 €)"
                  min="0"
                />
                {errors.default_salary_monthly_cents && <div style={{ color: 'red', fontSize: 12, marginTop: 4 }}>{errors.default_salary_monthly_cents}</div>}
                <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 4 }}>
                  Enter amount in cents (e.g., 300000 = 3000 €)
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Weekly Hours</label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.default_weekly_hours || ''}
                  onChange={(e) => handleInputChange('default_weekly_hours', e.target.value ? parseFloat(e.target.value) : undefined)}
                  style={inputStyle}
                  placeholder="e.g., 40"
                  min="0"
                />
              </div>
            </>
          )}

          <div>
            <label style={labelStyle}>Probation Period (months)</label>
            <input
              type="number"
              value={formData.default_probation_months || ''}
              onChange={(e) => handleInputChange('default_probation_months', e.target.value ? Number(e.target.value) : undefined)}
              style={inputStyle}
              placeholder="e.g., 6"
              min="0"
            />
          </div>
        </div>

        {/* Benefits */}
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: colors.navy }}>
            Benefits
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.default_benefits?.uniform || false}
                onChange={() => handleBenefitToggle('uniform')}
                style={{ marginRight: 8 }}
              />
              <span style={{ fontSize: 14, color: colors.navy }}>Uniform</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.default_benefits?.meal_allowance || false}
                onChange={() => handleBenefitToggle('meal_allowance')}
                style={{ marginRight: 8 }}
              />
              <span style={{ fontSize: 14, color: colors.navy }}>Essenszuschuss (Meal Allowance)</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.default_benefits?.service_vehicle || false}
                onChange={() => handleBenefitToggle('service_vehicle')}
                style={{ marginRight: 8 }}
              />
              <span style={{ fontSize: 14, color: colors.navy }}>Dienstwagen (Service Vehicle)</span>
            </label>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          padding: '16px 24px',
          borderTop: `1px solid ${colors.border}`,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 12,
          backgroundColor: colors.bgCard,
        }}
      >
        <button
          onClick={onClose}
          style={ps.buttonSecondary}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          style={saving ? ps.buttonDisabled : ps.buttonPrimary}
        >
          {saving ? 'Saving...' : isEdit ? 'Update Position' : 'Create Position'}
        </button>
      </div>

      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.3)',
          zIndex: -1,
        }}
      />
    </div>
  );
}

export default PositionEditorDrawer;
